import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Rocket, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BroadcastCard = ({ endpoint, tag, title, description, testIdPrefix, tone = "ember", onSent }) => {
  const [status, setStatus] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => axios.get(`${API}/admin/jobs/${endpoint}`).then((r) => setStatus(r.data)).catch(() => setStatus(null)), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/jobs/${endpoint}`, { confirm: typed.trim() });
      toast.success(`Sent to ${r.data.sent} member${r.data.sent === 1 ? "" : "s"}.`, {
        description: r.data.failed ? `${r.data.failed} failed — see the log below.` : "Every confirmed member is covered.",
      });
      setConfirming(false);
      setTyped("");
      load();
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send.");
    } finally {
      setBusy(false);
    }
  };

  const testToMe = async () => {
    try {
      const r = await axios.post(`${API}/admin/jobs/${endpoint}`, { test_to_me: true });
      toast[r.data.ok ? "success" : "message"](r.data.ok ? `Test copy sent to ${r.data.test_sent_to}.` : `Test copy logged for ${r.data.test_sent_to} (not deliverable from here).`);
    } catch {
      toast.error("Could not send the test copy.");
    }
  };

  if (!status) return null;

  return (
    <div className={`tacc-card p-6 md:p-8 ${tone === "green" ? "border-emerald-400/30" : "border-[#F59E0B]/30"}`} data-testid={`${testIdPrefix}-card`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="section-tag mb-2 flex items-center gap-2"><Rocket size={13} /> § {tag}</div>
          <div className="font-display text-2xl tracking-tight">{title}</div>
          <p className="text-[#78716C] text-sm mt-2 max-w-xl">{description}</p>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] mt-3" data-testid={`${testIdPrefix}-status`}>
            <span className="text-[#B45309]">{status.ready}</span> ready · {status.already_sent} already received
          </div>
        </div>
        {!confirming ? (
          <div className="flex flex-wrap gap-3">
            <button onClick={testToMe} className="btn-outline text-sm w-fit" data-testid={`${testIdPrefix}-test-btn`}>
              Send me a test copy
            </button>
            <button
              onClick={() => setConfirming(true)}
              disabled={!status.ready}
              className="btn-ember text-sm w-fit disabled:opacity-50"
              data-testid={`${testIdPrefix}-btn`}
            >
              <Rocket size={15} /> {status.ready ? `Send to ${status.ready} member${status.ready === 1 ? "" : "s"}` : "Everyone has it"}
            </button>
          </div>
        ) : (
          <div className="border border-red-500/40 bg-red-500/5 p-4 md:p-5 max-w-sm" data-testid={`${testIdPrefix}-confirm`}>
            <div className="flex items-center gap-2 text-red-300 font-mono text-[10px] tracking-[0.2em] uppercase mb-2">
              <ShieldAlert size={13} /> This cannot be undone
            </div>
            <p className="text-sm text-[#44403C] mb-3">Emails {status.ready} confirmed member{status.ready === 1 ? "" : "s"} right now. Type <strong>SEND</strong> to confirm.</p>
            <div className="flex flex-wrap gap-2">
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="SEND"
                data-testid={`${testIdPrefix}-confirm-input`}
                className="bg-[#FAF7F2] border border-[#E7DFD5] px-3 py-2 text-sm font-mono tracking-widest w-28 focus:outline-none focus:border-[#F59E0B]"
              />
              <button onClick={send} disabled={busy || typed.trim() !== "SEND"} className="btn-ember text-xs disabled:opacity-50" data-testid={`${testIdPrefix}-confirm-btn`}>
                Yes, send it
              </button>
              <button onClick={() => { setConfirming(false); setTyped(""); }} className="btn-outline text-xs" data-testid={`${testIdPrefix}-cancel`}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
